/*
 * Markdown -> plain text.
 *
 * Used for two things, neither of which needs a real parser:
 *
 *   - list excerpts, produced on the server so the browser does not download an
 *     nvarchar(max) per search result
 *   - the heading outline the editor's "insert article link" dialog offers when
 *     an author wants to link to a specific heading
 *
 * Rendering, by contrast, does use a real parser (marked) in the browser.
 */
import { toHeadingId } from './slug.js';
import { parseArticleLinkTokens } from './articleLinks.js';
import { parseAttachmentLinkTokens } from './attachmentLinks.js';
/** Stands in for an attachment label while the syntax around it is stripped. */
const LABEL_OPEN = '';
const LABEL_CLOSE = '';
const LABEL_PLACEHOLDER = /(\d+)/g;
/**
 * Flattens markdown to something readable in a single line of a list. Not a
 * spec-compliant renderer: it removes the syntax that would otherwise show up
 * as punctuation noise in a summary, and leaves everything else alone.
 */
export function markdownToPlainText(markdown, options = {}) {
    if (!markdown)
        return '';
    let text = markdown.replace(/\r\n?/g, '\n').replace(/[]/g, '');
    // Video tokens leave nothing behind: an excerpt is prose, and neither a URI
    // nor the word "video" is a summary of the article it appears in.
    text = text.replace(/\[\[video:[^\]\r\n]*?\]\]/g, '');
    // Article-link tokens become their display text, so an excerpt never shows
    // "[[kb:5f8c...]]" to a reader.
    for (const token of [...parseArticleLinkTokens(text)].sort((a, b) => b.index - a.index)) {
        const label = token.text ?? token.slug ?? '';
        text = text.slice(0, token.index) + label + text.slice(token.index + token.token.length);
    }
    // Attachment-link tokens (S47) become what the article page shows for them:
    // the link text, else the file name, else nothing. The label goes back in only
    // after the syntax below is stripped, because it is shown literally there - the
    // emphasis rule would take every underscore out of "Setup_Guide_v2.pdf".
    const labels = [];
    for (const token of [...parseAttachmentLinkTokens(text)].sort((a, b) => b.index - a.index)) {
        const label = token.text ?? (token.attachmentId ? options.attachmentNames?.get(token.attachmentId) : undefined) ?? '';
        const placeholder = label ? `${LABEL_OPEN}${labels.push(label) - 1}${LABEL_CLOSE}` : '';
        text = text.slice(0, token.index) + placeholder + text.slice(token.index + token.token.length);
    }
    return (text
        // Fenced code: drop the block entirely rather than quoting source in a summary.
        .replace(/^ {0,3}(```|~~~)[^\n]*\n[\s\S]*?^ {0,3}\1[^\n]*$/gm, '')
        // Unterminated fence at end of document.
        .replace(/^ {0,3}(```|~~~)[^\n]*\n[\s\S]*$/m, '')
        // HTML comments, then tags.
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<\/?[a-z][^>]*>/gi, '')
        // Images before links, so alt text does not survive as a caption.
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        // Reference-style links and their definitions.
        .replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, '')
        .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
        // Autolinks.
        .replace(/<((?:https?|mailto):[^>]+)>/g, '$1')
        // Inline code, keeping the code text.
        .replace(/`+([^`]*)`+/g, '$1')
        // ATX and setext headings.
        .replace(/^ {0,3}#{1,6}\s+/gm, '')
        .replace(/^ {0,3}[=-]{2,}\s*$/gm, '')
        // Block quotes, list bullets, numbered items, task boxes.
        .replace(/^ {0,3}>\s?/gm, '')
        .replace(/^\s*[*+-]\s+(\[[ xX]\]\s+)?/gm, '')
        .replace(/^\s*\d+[.)]\s+/gm, '')
        // Thematic breaks and table pipes.
        .replace(/^ {0,3}([*_-])\s*(?:\1\s*){2,}$/gm, '')
        .replace(/^\s*\|?[\s:|-]{3,}\|?\s*$/gm, '')
        .replace(/\|/g, ' ')
        // Emphasis markers, then the backslash escapes that protected them.
        .replace(/(\*\*\*|___|\*\*|__|\*|_|~~)/g, '')
        .replace(/\\([\\`*_{}[\]()#+\-.!<>|~])/g, '$1')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{2,}/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .join(' ')
        .trim()
        .replace(LABEL_PLACEHOLDER, (_placeholder, n) => labels[Number(n)] ?? ''));
}
/**
 * The plain-text PROJECTION a search runs against - stored on the revision as
 * kb.ArticleRevision.SearchText and indexed from kb.ArticleSearch.
 *
 * It differs from markdownToPlainText in what it keeps. An excerpt drops fenced
 * code and image alt text because neither summarises an article; a search must
 * keep both, because the error message an author quoted in a code block is the
 * single most searched-for thing in a knowledge base, and the alt text is the
 * words the author wrote about the picture. Only the fence lines and the image
 * file name go. Everything else - tokens to their labels, links to their text,
 * syntax removed, whitespace collapsed - is the excerpt's rule, applied once.
 * An [[attachment:...]] token contributes its link text only, never a file name:
 * the projection is stored, and replacing a file may rename it.
 *
 * Computed by the server when it saves a revision, never by the browser: the
 * projection is a property of the stored text, and the text is trusted only once
 * it has been written.
 */
export function markdownToSearchText(markdown) {
    if (!markdown)
        return '';
    const text = markdown
        .replace(/\r\n?/g, '\n')
        // Fence lines only; the code between them stays as words.
        .replace(/^ {0,3}(?:```|~~~)[^\n]*$/gm, '')
        // An image contributes its alt text, not its destination.
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
    return markdownToPlainText(text);
}
/**
 * First `maxLength` characters of the plain text, cut on a word boundary and
 * suffixed with a horizontal ellipsis when anything was dropped.
 */
export function buildExcerpt(markdown, maxLength = 260, options = {}) {
    const text = markdownToPlainText(markdown, options);
    if (text.length <= maxLength)
        return text;
    const clipped = text.slice(0, maxLength);
    const lastSpace = clipped.lastIndexOf(' ');
    const head = lastSpace > maxLength * 0.5 ? clipped.slice(0, lastSpace) : clipped;
    return `${head.replace(/[\s,;:.–—-]+$/, '')}…`;
}
/**
 * The heading outline, skipping headings inside fenced code blocks. Ids are
 * de-duplicated the way the renderer must also do it, so two "Notes" headings
 * get "notes" and "notes-1". The renderer takes a heading's id from its rendered
 * text, so a heading with an unlabelled attachment link matches only when the
 * file names are passed.
 */
export function extractHeadings(markdown, options = {}) {
    if (!markdown)
        return [];
    const headings = [];
    const seen = new Map();
    let fenceMarker = null;
    for (const rawLine of markdown.replace(/\r\n?/g, '\n').split('\n')) {
        const fenceMatch = /^ {0,3}(`{3,}|~{3,})/.exec(rawLine);
        if (fenceMarker === null && fenceMatch) {
            fenceMarker = (fenceMatch[1] ?? '').charAt(0);
            continue;
        }
        if (fenceMarker !== null) {
            if (fenceMatch && (fenceMatch[1] ?? '').startsWith(fenceMarker))
                fenceMarker = null;
            continue;
        }
        const headingMatch = /^ {0,3}(#{1,6})\s+(.+?)\s*#*\s*$/.exec(rawLine);
        if (!headingMatch)
            continue;
        const level = (headingMatch[1] ?? '#').length;
        const text = markdownToPlainText(headingMatch[2] ?? '', options);
        if (text.length === 0)
            continue;
        const base = toHeadingId(text) || 'section';
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        headings.push({ level, text, id: count === 0 ? base : `${base}-${count}` });
    }
    return headings;
}
/** Rough reading time in minutes, at 200 words per minute, minimum 1. */
export function estimateReadingMinutes(markdown) {
    const words = markdownToPlainText(markdown).split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
}
//# sourceMappingURL=markdownText.js.map